import { useId, useState } from 'react';
import Icon from '../ui/Icon';
import StatusBadge from '../ui/StatusBadge';
import { copyText } from '../../utils/clipboard';
import { CONFIDENCE, engineLabel, ocrStats } from './scanAdapter';

const VALUE_TONES = { ok: 'text-secondary', alert: 'text-error', pending: 'text-status-pending-text', neutral: 'text-on-surface' };

/** OCR engine, confidence, four check tiles and the collapsible raw text. */
export default function OcrCard({ ocr, ocrText }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const confidence = CONFIDENCE[ocr.confidence] ?? { label: ocr.confidence, tone: 'neutral' };

  return (
    <section aria-label="OCR result" className="rounded-xl bg-surface-container-low p-3 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-on-surface">
          <Icon name="document_scanner" size={18} className="text-secondary" />
          OCR Engine: {engineLabel(ocr.engine)}
        </h3>
        <StatusBadge tone={confidence.tone}>{confidence.label} confidence</StatusBadge>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {ocrStats(ocr).map((stat) => (
          <div key={stat.key} className="rounded-lg bg-surface-container-lowest px-2 py-2.5 text-center">
            <dt className="text-[11px] font-data-mono text-on-surface-variant">{stat.label}</dt>
            <dd className={`mt-0.5 font-data-mono text-sm font-bold ${VALUE_TONES[stat.tone]}`}>{stat.value}</dd>
          </div>
        ))}
      </dl>

      <div>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-2 px-1 py-1 rounded text-sm text-on-surface hover:text-primary outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
        >
          <span className="flex items-center gap-1.5">
            <Icon name="terminal" size={18} />
            Raw Parsed Text Stream
          </span>
          <Icon name={open ? 'expand_less' : 'expand_more'} size={20} />
        </button>
        {open && (
          <div id={panelId} className="relative mt-2">
            {ocrText ? (
              <>
                <pre className="max-h-72 overflow-auto rounded-lg bg-surface-container-lowest border border-outline-variant/30 p-3 pr-10 font-data-mono text-[11px] leading-relaxed text-on-surface whitespace-pre-wrap break-words">
                  {ocrText}
                </pre>
                <button
                  type="button"
                  onClick={() => copyText(ocrText, 'OCR text copied')}
                  aria-label="Copy OCR text"
                  title="Copy"
                  className="absolute top-2 right-2 p-1 rounded bg-surface-container-lowest text-on-surface-variant hover:text-primary outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
                >
                  <Icon name="content_copy" size={16} />
                </button>
              </>
            ) : (
              <p className="px-1 text-xs text-on-surface-variant">OCR didn&apos;t return any text for this receipt.</p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
